import React from 'react'
import ReactDOM from 'react-dom'
import styled, { withTheme } from 'styled-components'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import Card from '@jarvis.oddle/core/components/Card'
import Flex from '@jarvis.oddle/core/components/Flex'
import Div from '@jarvis.oddle/core/components/Div'
import Dropdown from '@jarvis.oddle/core/components/Dropdown'
import ResponsiveSection from '@jarvis.oddle/core/components/ResponsiveSection'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import IconArrow, {
  arrowDirections
} from '@jarvis.oddle/core/components/Icons/IconArrow'
import { H4 } from '@jarvis.oddle/core/components/Typo'
import {
  borderRadiusSizes,
  horizontalNav,
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import WindowEvents from '@jarvis.oddle/core/decorators/WindowEvents'
import VerticalNavigation from 'src/components/molecules/VerticalNavigation'

const Container = styled(Card).attrs(() => ({
  borderRadius: borderRadiusSizes.small
}))`
  height: ${horizontalNav.height};
  padding: ${padding[paddingSizes.none]} ${padding[paddingSizes.medium]};
  ${breakpoint('desktop')`
    padding: ${padding[paddingSizes.none]} ${padding[paddingSizes.medium]};
  `}
`

const StyledResponsiveSection = styled(ResponsiveSection)`
  && {
    margin: 0;
  }
`

const InnerContainer = styled(ResponsiveSection.Container)`
  ${(props) =>
    props.scrollEnabled
      ? `
    overflow-x: auto;
  `
      : ''}
`

const CategoryContainer = styled(Div)`
  height: ${horizontalNav.height};
  display: block;
  overflow-y: hidden;
`

const NavItem = styled(Div)`
  height: ${horizontalNav.height};
  white-space: nowrap;
  text-decoration: none;
  padding: ${padding[paddingSizes.medium]};
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  border-bottom: 4px solid
    ${(props) => (props.selected ? props.theme.buttonColor : 'transparent')};
  color: ${(props) =>
    props.selected ? props.theme.buttonColor : props.theme.colorHeadingsFont};

  &:hover,
  &:focus {
    cursor: pointer;
    pointer-events: auto;
  }
`

const NavItemText = styled(H4)`
  color: inherit;

  &:hover,
  &:focus {
    color: ${(props) => props.theme.buttonColor};
  }
`

const StyledLink = styled.a`
  text-decoration: none;
`

const MoreButton = styled(Flex).attrs(() => ({
  width: 'max-content',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center'
}))`
  height: ${horizontalNav.height};
  white-space: nowrap;
  padding: ${padding[paddingSizes.medium]};
  border-bottom: 4px solid transparent;

  ${(props) =>
    props.selected
      ? `
    border-bottom-color: ${props.theme.buttonColor};
    color: ${props.theme.buttonColor};
  `
      : `
    color: ${props.theme.colorHeadingsFont};
  `}
  &:hover,
  &:focus {
    cursor: pointer;
    pointer-events: auto;
  }
`

const RightContainer = styled(Flex).attrs(() => ({
  flexDirection: 'row',
  justifyContent: 'flex-end'
}))``

const StyledVerticalNavigation = styled(VerticalNavigation)`
  min-width: 200px;
  max-width: 280px;
  float: right;
`

const DropdownLabel = withTheme(
  ({ labelText, onClick, isDropdownOpening, selected }) => (
    <MoreButton onClick={onClick} selected={selected}>
      <NavItemText
        bold
        margin={marginSizes.none}
        marginRight={marginSizes.small}
      >
        {labelText}
      </NavItemText>
      <IconArrow
        iconSize='small'
        fill='currentColor'
        direction={
          isDropdownOpening ? arrowDirections.up : arrowDirections.down
        }
      />
    </MoreButton>
  )
)

const DropdownContainer = ({
  isDropdownOpening,
  items,
  onClickItem
}: {
  isDropdownOpening: boolean
  items: Record<string, string>[]
  onClickItem: (event, id) => void
}) =>
  isDropdownOpening ? (
    <StyledVerticalNavigation
      items={items}
      onClickItem={onClickItem}
    />
  ) : null

const HorizontalNavigation = ({
  className,
  items = [],
  selectedItemId = '',
  onClickItem,
  registerEventListener
}: {
  className: string
  items: Record<string, string>[]
  selectedItemId: string
  onClickItem: (event, id) => void
  registerEventListener: (name, handler, option) => void
}): JSX.Element => {
  const { t } = React.useContext(TranslationContext)
  const listRef = React.useRef({})
  const [state, setState] = React.useReducer(
    (state, nextState) => ({ ...state, ...nextState }),
    {
      isDropdownVisible: false,
      displayedItems: [],
      hiddenItems: [],
      selectedItemId
    }
  )

  const updateNavigationItems = React.useCallback(() => {
    const hiddenItems = []
    items.forEach((item) => {
      const dom = ReactDOM.findDOMNode(listRef.current[item.id]) as HTMLElement
      if (dom && dom.offsetTop > 0) {
        hiddenItems.push(item)
      }
    })

    setState({
      isDropdownVisible: hiddenItems.length > 0,
      hiddenItems
    })
  }, [items])

  React.useEffect(() => {
    registerEventListener('resize', updateNavigationItems, true)
    updateNavigationItems()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if (state.selectedItemId !== selectedItemId) {
      updateNavigationItems()
    }
  }, [selectedItemId, state.selectedItemId, updateNavigationItems])

  const selectedCategory = items.find((c) => c.id === selectedItemId)
  const labelText =
    selectedCategory && state.hiddenItems.some((i) => i.id === selectedCategory.id)
      ? selectedCategory.name
      : ''

  return (
    <Container className={className} id='product-section'>
      <StyledResponsiveSection>
        <InnerContainer
          ref={(ref) => {
            listRef.current['container'] = ref
          }}
          scrollEnabled={!state.isDropdownVisible}
        >
          <Flex flexDirection='row'>
            <CategoryContainer>
              {(items || []).map((cat) => (
                <StyledLink key={cat.id} href={cat.href}>
                  <NavItem
                    ref={(ref) => {
                      listRef.current[cat.id] = ref
                    }}
                    selected={cat.id === selectedItemId}
                    onClick={(e) => onClickItem(e, cat.id)}
                  >
                    <NavItemText bold margin={marginSizes.none}>
                      {cat.name}
                    </NavItemText>
                  </NavItem>
                </StyledLink>
              ))}
            </CategoryContainer>
            {state.isDropdownVisible && (
              <RightContainer>
                <Dropdown
                  renderLabel={({ isDropdownOpening, toggleDropdown }) => (
                    <DropdownLabel
                      isDropdownOpening={isDropdownOpening}
                      onClick={toggleDropdown}
                      labelText={labelText !== '' ? labelText : t('More')}
                      selected={labelText !== ''}
                    />
                  )}
                  renderDropdown={({ isDropdownOpening }) => (
                    <DropdownContainer
                      isDropdownOpening={isDropdownOpening}
                      items={state.hiddenItems}
                      onClickItem={onClickItem}
                    />
                  )}
                />
              </RightContainer>
            )}
          </Flex>
        </InnerContainer>
      </StyledResponsiveSection>
    </Container>
  )
}

export default WindowEvents(HorizontalNavigation)
